import dummyData from "../data/dummy-data.json";
import DEMO_CONFIG from "../config/demo";
import DemoDataManager from "./DemoDataManager";

// Mock API service that simulates backend responses using dummy data
class MockApiService {
  constructor() {
    this.isDemoMode = true;
    this.delay = DEMO_CONFIG.MOCK_DELAY || 500; // Simulate network delay (ms)
    this.successRate = DEMO_CONFIG.MOCK_SUCCESS_RATE || 1.0;
    // Initialize demo data manager
    DemoDataManager.initializeData();
  }

  // Simulate async network call
  async mockFetch(data, successRate = null) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Simulate occasional failures for realism
        const rate = successRate !== null ? successRate : this.successRate;
        if (Math.random() < rate) {
          resolve(data);
        } else {
          reject({
            status: false,
            message: "Network error",
          });
        }
      }, this.delay);
    });
  }

  // Auth APIs
  async login(payload) {
    const userData = DemoDataManager.getUser();
    const walletData = DemoDataManager.getWalletData();

    const response = {
      ...dummyData.auth.login,
      data: {
        ...dummyData.auth.login.data,
        user: { ...userData },
        wallets: { ...walletData },
      },
    };
    return this.mockFetch(response);
  }

  async register(payload) {
    return this.mockFetch(dummyData.auth.register);
  }

  async confirmMail(tk, em) {
    return this.mockFetch(dummyData.auth.verify_email);
  }

  async checkUsername(username) {
    return this.mockFetch(dummyData.auth.check_username);
  }

  async setUsername(payload) {
    const userData = DemoDataManager.updateUser({
      username: payload.username || dummyData.user.username,
    });

    const response = {
      ...dummyData.auth.login,
      data: {
        ...dummyData.auth.login.data,
        user: { ...userData },
      },
    };
    return this.mockFetch(response);
  }

  async twoFactorSetup(authType, value) {
    return this.mockFetch(dummyData.auth.two_factor);
  }

  async twoFactorConfirmation(authType, value, token) {
    return this.mockFetch(dummyData.auth.two_factor_confirm);
  }

  async resetPassword(email) {
    return this.mockFetch(dummyData.auth.reset_password);
  }

  async changePassword(payload) {
    return this.mockFetch(dummyData.auth.change_password);
  }

  async confirmResetPassword(payload) {
    return this.mockFetch(dummyData.auth.change_password);
  }

  async logout() {
    return this.mockFetch({ status: true, message: "Logged out successfully" });
  }

  async logoutAllSessions() {
    return this.mockFetch({ status: true, message: "All sessions logged out" });
  }

  // Wallet APIs
  async getPulsar() {
    const walletData = DemoDataManager.getWalletData();

    const response = {
      status: true,
      data: {
        balance: walletData.pulsar_data.balance,
        addresses: [...walletData.pulsar_data.addresses],
      },
    };
    return this.mockFetch(response);
  }

  async generateAddress() {
    return this.mockFetch(dummyData.wallet_operations.generate_address);
  }

  async sendPulsar(payload) {
    // Handle both payload structures: payload.values or direct payload
    const values = payload.values || payload;

    // Calculate transaction details
    const amount = parseFloat(values.amount);
    const fee = 0.0001;
    const totalDeducted = amount + fee;

    // Update balance in demo data
    const newBalance = DemoDataManager.updateBalance(totalDeducted, "subtract");

    // Add transaction to history
    const transaction = DemoDataManager.addTransaction({
      type: "send",
      amount: amount,
      fee: fee,
      to_address: values.to_address || "Dev Support",
      status: "completed",
      confirmations: 1,
    });

    return this.mockFetch({
      ...dummyData.wallet_operations.send_transaction,
      data: {
        ...dummyData.wallet_operations.send_transaction.data,
        amount: amount,
        to_address: values.to_address,
        new_balance: newBalance,
        transaction_id: transaction.id,
      },
    });
  }

  async getTransactions() {
    const transactions = DemoDataManager.getTransactions();

    const response = {
      status: true,
      data: {
        transactions: [...transactions],
      },
    };
    return this.mockFetch(response);
  }

  async getExplorerData() {
    const explorerData = DemoDataManager.getExplorerData();

    const response = {
      status: true,
      data: { ...explorerData },
    };
    return this.mockFetch(response);
  }

  async searchPulsar(keyword) {
    const filteredResults = dummyData.search_results.filter(
      (item) =>
        item.username.toLowerCase().includes(keyword.toLowerCase()) ||
        item.address.toLowerCase().includes(keyword.toLowerCase()),
    );
    return this.mockFetch({
      status: true,
      data: filteredResults,
    });
  }

  async explorerRecords() {
    return this.mockFetch({
      status: true,
      data: dummyData.explorer_records,
    });
  }

  async verifyAddress(address) {
    return this.mockFetch(dummyData.wallet_operations.verify_address);
  }

  // Staking APIs
  async stake(payload) {
    const amount = parseFloat(payload.amount);

    // Update staking amount (this also deducts from balance)
    const newStakedAmount = DemoDataManager.updateStaking(amount, "add");

    // Add transaction to history
    const transaction = DemoDataManager.addTransaction({
      type: "stake",
      amount: amount,
      status: "completed",
      confirmations: 1,
    });

    return this.mockFetch({
      ...dummyData.staking.stake_transaction,
      data: {
        ...dummyData.staking.stake_transaction.data,
        staked_amount: newStakedAmount,
        transaction_id: transaction.id,
      },
    });
  }

  async userStakeDetails() {
    const stakingData = DemoDataManager.getStakingData();

    return this.mockFetch({
      ...dummyData.staking.user_stake_details,
      data: {
        ...dummyData.staking.user_stake_details.data,
        user: {
          ...dummyData.staking.user_stake_details.data.user,
          user_stake_balance:
            stakingData.user_stake_details.data.user.user_stake_balance,
        },
      },
    });
  }

  async withdrawStake(payload) {
    const withdrawAmount = parseFloat(payload.amount);
    const fee = 0.0001;
    const stakingData = DemoDataManager.getStakingData();
    const currentStakeBalance =
      stakingData.user_stake_details.data.user.user_stake_balance;

    if (withdrawAmount <= currentStakeBalance) {
      // Reduce staked amount (this also adds back to balance)
      DemoDataManager.updateStaking(withdrawAmount, "subtract");

      // Deduct fee from balance
      DemoDataManager.updateBalance(fee, "subtract");

      // Add transaction to history
      const transaction = DemoDataManager.addTransaction({
        type: "unstake",
        amount: withdrawAmount,
        fee: fee,
        status: "completed",
        confirmations: 1,
      });

      return this.mockFetch({
        status: true,
        message: "Withdrawal successful",
        data: {
          ...dummyData.staking.withdraw_transaction,
          amount: withdrawAmount,
          transaction_id: transaction.id,
        },
      });
    } else {
      return Promise.reject({
        status: false,
        message: "Insufficient stake balance",
      });
    }
  }

  // Settings APIs
  async hideUsername(otp) {
    DemoDataManager.updateUser({ hide_username: true });
    return this.mockFetch(dummyData.settings.hide_username);
  }

  async showUsername(otp) {
    DemoDataManager.updateUser({ hide_username: false });
    return this.mockFetch(dummyData.settings.show_username);
  }

  async transferAllCoins(payload) {
    return this.mockFetch(dummyData.settings.transfer_coins);
  }

  async deleteAccount(otp) {
    return this.mockFetch(dummyData.settings.delete_account);
  }

  // Google Auth APIs
  async enableGoogleTwoFactor() {
    dummyData.user.google_two_factor = true;
    return this.mockFetch(dummyData.google_auth.two_factor_enable);
  }

  async disableGoogleTwoFactor(otp) {
    dummyData.user.google_two_factor = false;
    return this.mockFetch(dummyData.google_auth.two_factor_disable);
  }

  // Admin APIs
  async sendNotification(payload) {
    const newNotification = {
      id: `notif-${Date.now()}`,
      title: payload.title,
      message: payload.message,
      type: payload.type || "info",
      read: false,
      createdAt: new Date().toISOString(),
    };
    dummyData.notifications.unshift(newNotification);

    return this.mockFetch({
      status: true,
      message: "Notification sent successfully",
      data: newNotification,
    });
  }

  async getNotification(id) {
    const notification = dummyData.notifications.find((n) => n.id === id);
    return this.mockFetch({
      status: true,
      data: notification || null,
    });
  }

  async getAllNotifications() {
    return this.mockFetch({
      status: true,
      data: [...dummyData.notifications],
    });
  }
}

// Create singleton instance
const mockApiService = new MockApiService();

export default mockApiService;
